<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useDisplay } from 'vuetify'
import { useNotificationSoundStore } from '../../stores/notificationSoundStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'

/**
 * Все настройки звука хранятся в localStorage через notificationSoundStore — изменения
 * применяются мгновенно без обращения к беку (бек звук пока не поддерживает).
 *
 * Мастер-свитч `enabled` вверху управляет всеми остальными: при выключенном свитче
 * категории и DnD дизейблятся в UI и игнорируются при решении проигрывать ли звук.
 */

const store = useNotificationSoundStore()
const feedback = useFeedbackStore()
const { enabled, critical, important, info, dnd } = storeToRefs(store)
const { mobile } = useDisplay()

const isMasterOff = computed(() => !enabled.value)

const masterValue = computed({
  get: () => enabled.value,
  set: v => store.setEnabled(v),
})
const criticalValue = computed({
  get: () => critical.value,
  set: v => store.setCategoryEnabled('critical', v),
})
const importantValue = computed({
  get: () => important.value,
  set: v => store.setCategoryEnabled('important', v),
})
const infoValue = computed({
  get: () => info.value,
  set: v => store.setCategoryEnabled('info', v),
})
const dndEnabledValue = computed({
  get: () => dnd.value.enabled,
  set: v => store.setDndEnabled(v),
})
const dndFromValue = computed({
  get: () => dnd.value.from,
  set: v => store.setDndRange(v, dnd.value.to),
})
const dndToValue = computed({
  get: () => dnd.value.to,
  set: v => store.setDndRange(dnd.value.from, v),
})

function onSave(): void {
  // настройки уже сохранены в localStorage по каждому toggle — здесь только подтверждение
  feedback.showToast({ type: 'success', message: 'Настройки звука сохранены' })
}
</script>

<template>
  <VCard
    elevation="0"
    class="sound-card"
  >
    <div class="d-flex flex-column ga-2 pa-4">
      <!-- Мастер-свитч -->
      <div class="sound-row d-flex align-start ga-3">
        <div class="flex-grow-1">
          <div class="sound-row__title">
            Все уведомления
          </div>
        </div>
        <VSwitch
          v-model="masterValue"
          inset
          hide-details
          density="compact"
        />
      </div>

      <!-- Критичные -->
      <div class="sound-row d-flex align-start ga-3">
        <div class="flex-grow-1">
          <div class="sound-row__title">
            Критичные
          </div>
          <div class="sound-row__subtitle">
            Уведомления по НС, заявкам и аудитам
          </div>
        </div>
        <VSwitch
          v-model="criticalValue"
          :disabled="isMasterOff"
          inset
          hide-details
          density="compact"
        />
      </div>

      <!-- Важные -->
      <div class="sound-row d-flex align-start ga-3">
        <div class="flex-grow-1">
          <div class="sound-row__title">
            Важные
          </div>
          <div class="sound-row__subtitle">
            Уведомления по событиям в календаре
          </div>
        </div>
        <VSwitch
          v-model="importantValue"
          :disabled="isMasterOff"
          inset
          hide-details
          density="compact"
        />
      </div>

      <!-- Информационные -->
      <div class="sound-row d-flex align-start ga-3">
        <div class="flex-grow-1">
          <div class="sound-row__title">
            Информационные
          </div>
          <div class="sound-row__subtitle">
            Уведомления о новостях и ответы на предложения
          </div>
        </div>
        <VSwitch
          v-model="infoValue"
          :disabled="isMasterOff"
          inset
          hide-details
          density="compact"
        />
      </div>

      <!-- Режим «Не беспокоить» -->
      <div class="sound-row d-flex align-start ga-3">
        <div class="flex-grow-1 d-flex flex-column ga-2">
          <div>
            <div class="sound-row__title">
              Режим «Не беспокоить»
            </div>
            <div class="sound-row__subtitle">
              Установите период
            </div>
          </div>
          <div class="d-flex ga-2">
            <VTextField
              v-model="dndFromValue"
              type="time"
              density="compact"
              hide-details
              :disabled="isMasterOff || !dndEnabledValue"
              variant="outlined"
              class="sound-row__time"
              prefix="с"
            />
            <VTextField
              v-model="dndToValue"
              type="time"
              density="compact"
              hide-details
              :disabled="isMasterOff || !dndEnabledValue"
              variant="outlined"
              class="sound-row__time"
              prefix="по"
            />
          </div>
        </div>
        <VSwitch
          v-model="dndEnabledValue"
          :disabled="isMasterOff"
          inset
          hide-details
          density="compact"
        />
      </div>

      <!-- Кнопка сохранения внутри карточки -->
      <div class="d-flex">
        <VBtn
          color="primary"
          :block="mobile"
          @click="onSave"
        >
          Сохранить
        </VBtn>
      </div>
    </div>
  </VCard>
</template>

<style scoped lang="scss">
.sound-card {
  inline-size: 100%;
  max-inline-size: 480px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 8px;
}

.sound-row {
  padding-block: 8px;

  & + & {
    border-block-start: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  }

  &__title {
    font-size: 14px;
    line-height: 20px;
    color: rgba(var(--v-theme-on-surface), 0.87);
  }

  &__subtitle {
    font-size: 12px;
    line-height: 18px;
    color: rgba(var(--v-theme-on-surface), 0.55);
  }

  &__time {
    max-inline-size: 140px;
  }
}
</style>
